/**
 * Format: Variant
 * -----------------------------------------------------------------------------
 * Formats Shopify storefront API variants response.
 *
 */
import { formatImage } from '~/format/format-image';
import { formatMedia } from '~/format/format-media';
import { sortOptionNames } from '~/format/format-option';
import { formatMoneyV2 } from '~/format/format-price';
import { formatProductBadges, formatProductUrl } from '~/format/format-product';
import { convertToCamelCase } from '~/helpers/convert';
import {
  isVisibleInCurrentMarket,
  normalizeMarketCode,
} from '~/helpers/general';
import { decodeApiId, transformEdges } from '~/helpers/graphql';
import { formatRichtext } from '~/core/helpers/format-richtext';

/**
 * Parses a JSON-list metafield value, falling back to `[]` if it's missing
 * or malformed (e.g. a metafield defined/entered as plain text rather than
 * a JSON list).
 * @param {String} [value]
 * @returns {Array}
 */
function parseJsonList(value) {
  if (!value) {
    return [];
  }

  try {
    return JSON.parse(value);
  } catch {
    return [];
  }
}

/**
 * Format product variants array.
 * - https://shopify.dev/docs/api/storefront/2024-01/objects/ProductVariant
 * @param {Object} product - Product response.
 * @param {Object} [collection] - Formatted collection object.
 * @returns {Array}
 */
export function formatVariants(product, collection) {
  if (!product?.variants) {
    return [];
  }

  return transformEdges(product.variants).map((variant) => {
    return formatVariant(variant, product, collection);
  });
}

/**
 * Format product variant.
 * - https://shopify.dev/docs/api/storefront/2024-01/objects/ProductVariant
 * @param {Object} variant - Variant response.
 * @param {Object} product - Product response.
 * @returns {Object}
 */
function formatVariant(variant, product) {
  const formattedVariant = {
    aminoAcidValues: variant.aminoAcidValues?.value,
    available: variant.available,
    badges: formatProductBadges(variant),
    canDeposit: variant.canDeposit?.value,
    canDepositAmount: variant.canDepositAmount?.value,
    compareAtPrice: formatMoneyV2(variant.compareAtPrice),
    featuredImage: formatImage(variant?.featuredImage),
    flavorImage: formatImage(variant.flavorImage?.reference?.image) || false,
    alternativeFlavorImage:
      formatImage(variant.alternativeFlavorImage?.reference?.image) || false,
    customImage: formatImage(variant.customImage?.reference?.image) || false,
    flavorBadge: variant.flavorBadge?.value || false,
    flavors: parseJsonList(variant.flavors?.value),
    genericTags: parseJsonList(variant.genericTags?.value),
    localizedImage: formatMedia(variant.localizedImage?.references, false)[0]
      ?.image,
    abTestImage: formatMedia(variant.abTestImage?.references, false)[0]?.image,
    gid: variant.id,
    id: decodeApiId(variant.id),
    ingredients: variant.ingredients?.value,
    designationOfFood: variant.designationOfFood?.value,
    inventory: variant.quantityAvailable,
    lowInventoryThreshold: variant.lowInventoryThreshold?.value,
    initialInventory: variant.initialInventory?.value
      ? Number.parseInt(variant.initialInventory?.value)
      : null,
    maxQuantity: variant.maxQuantity?.value
      ? Number.parseInt(variant.maxQuantity.value)
      : null,
    media: formatMedia(variant.media?.references, false),
    nutritionValues: variant.nutritionValues?.value,
    vitaminValues: variant.vitaminValues?.value,
    mineralValues: variant.mineralValues?.value,
    oosAlternativeVariantIds: parseJsonList(variant.oosAlternatives?.value),
    portions: variant.portions?.value,
    price: formatMoneyV2(variant.price),
    selectedOptions: formatSelectedOptions(variant.selectedOptions),
    sku: variant.sku ?? false,
    title: stripTrailingParenthetical(variant.title),
    unitPrice: formatMoneyV2(variant.unitPrice),
    unitPriceMeasurement: variant.unitPriceMeasurement,
  };
  /**
   * Determine URL.
   */
  const url = `//${location.host}${cnvs.routes.normalisedRoot}products/${product.handle}?variant=${formattedVariant.id}`;

  formattedVariant.url = formatProductUrl(url, product.trackingParameters);

  /**
   * Determine available state.
   */
  formattedVariant.availableState = getVariantAvailableState(
    variant.availability,
    variant.available,
    formattedVariant.price?.amount,
  );

  /**
   * Set available to false if sold out.
   */
  if (formattedVariant.availableState !== 'available') {
    formattedVariant.available = false;
  }

  /**
   * Determine compare at price discount.
   * - (40 - 30) = 10
   * - (10 / 40) = 0.25
   * - (0.25 * 100) = 25% compare at price discount
   */
  formattedVariant.compareAtPriceDiscount =
    formattedVariant.compareAtPrice?.amount > formattedVariant.price?.amount
      ? ((formattedVariant.compareAtPrice.amount -
          formattedVariant.price.amount) /
          formattedVariant.compareAtPrice.amount) *
        100
      : 0;

  formattedVariant.compareAtPriceDiscountRounded = Math.round(
    formattedVariant.compareAtPriceDiscount,
  );

  /**
   * Product social selling.
   */
  const socialSellingObject = variant.socialSelling?.reference?.fields;
  let icon = null;
  let description = null;
  let showWhenSoldOut = false;
  let onlyShowInSelectedMarkets;

  if (socialSellingObject) {
    for (const social of socialSellingObject) {
      if (social.key === 'icon') {
        icon = social.reference?.image?.url;
      }

      if (social.key === 'description') {
        description = formatRichtext(social.value);
      }

      if (social.key === 'show_when_sold_out') {
        showWhenSoldOut = social.value === 'true';
      }

      if (social.key === 'only_show_in_selected_markets') {
        onlyShowInSelectedMarkets = social.value;
      }
    }
  }

  if (
    icon &&
    description &&
    isVisibleInCurrentMarket(onlyShowInSelectedMarkets)
  ) {
    formattedVariant.socialSelling = {
      icon,
      description,
      showWhenSoldOut,
    };
  }

  return formattedVariant;
}

/**
 * Strip a trailing " (anytext)" segment from an option value.
 * @param {String} value - Original option value.
 * @returns {String}
 */
export function stripTrailingParenthetical(value) {
  const trimmed = value.trimEnd();

  if (!trimmed.endsWith(')')) {
    return value;
  }

  const openIdx = trimmed.lastIndexOf('(');

  if (openIdx <= 0) {
    return value;
  }

  return trimmed.slice(0, openIdx).trimEnd();
}

/**
 * Format selection options into sorted order.
 * @param {Array} selectedOptions - Selected options from response.
 * @returns {Array}
 */
function formatSelectedOptions(selectedOptions) {
  return selectedOptions
    .map((option, index) => {
      return {
        name: option.name,
        originalIndex: index,
        value: stripTrailingParenthetical(option.value),
      };
    })
    .sort(sortOptionNames)
    .map((option, index) => {
      return {
        ...option,
        index,
      };
    });
}

/**
 * @param {Array<String>} countryList - Array with 2-letter country
 * codes where variant is hidden, e. g. ["UK","NL","FR"] (ISO 3166-1 alpha-2,
 * except the United Kingdom which we standardise to "UK").
 * @returns {Boolean}
 */
function isOnCountryList(countryList) {
  if (!Array.isArray(countryList) || countryList.length === 0) {
    return false;
  }

  // Shopify reports the UK as 'GB'; normalise both sides to 'UK' so the
  // comparison reads consistently regardless of which value the data uses.
  const normalizedList = countryList.map(normalizeMarketCode);
  const currentCountry = normalizeMarketCode(cnvs.store.country);

  return (
    normalizedList.includes('ALL') || normalizedList.includes(currentCountry)
  );
}

/**
 *
 * @param {String} countryString - List of comma separated country codes where
 * variant is hidden, e. g. "GB,NL,FR" (ISO 3166-1 alpha-2)
 * @returns {Array<String>} - Array with sanitized country codes.
 */
function countryStringToArray(countryString) {
  return countryString.split(',').map((value) => value.trim().toUpperCase());
}

/**
 * Get variant available state.
 * - 'available' - Show as usual.
 * - 'back-soon' - Out of stock.
 * - 'sold-out' - Out of stock.
 * - 'hidden' - Hide from front-end.
 * @param {Array} metafields - Relevant metafields.
 * @param {Boolean} available - Variant available field.
 * @returns {String}
 */
export function getVariantAvailableState(metafields, available, price) {
  if (!price) {
    return 'hidden';
  }

  const availability = {};
  const now = new Date().getTime();

  const processAvailabilityMetafields = () => {
    if (metafields?.length) {
      metafields.forEach((metafield) => {
        if (!metafield) {
          return;
        }

        const key = convertToCamelCase(metafield.key.replaceAll('_', '-'));
        availability[key] = metafield.value === 'true' ? true : metafield.value;
      });
    }
  };
  processAvailabilityMetafields();

  const processCountryLists = () => {
    ['marketsHideVariant', 'marketsPublishVariant'].forEach((key) => {
      if (availability[key]) {
        availability[key] = countryStringToArray(availability[key]);
      }
    });
  };
  processCountryLists();

  // "Hide Variant" trumps all other metafields
  if (isOnCountryList(availability.marketsHideVariant)) {
    return 'hidden';
  }

  availability.publishTime = availability.marketsPublishDate
    ? new Date(availability.marketsPublishDate)?.getTime()
    : 0;

  const setSoldOutVars = () => {
    const warehouse =
      normalizeMarketCode(cnvs.store.marketCode) === 'UK' ? 'Uk' : 'Elmshorn';
    const relaunchDate = availability[`setRelaunchDate${warehouse}`];

    availability.relaunchTime = relaunchDate
      ? new Date(relaunchDate)?.getTime()
      : 0;
    availability.showSoldOut = availability[`showSoldOut${warehouse}`];
  };
  setSoldOutVars();

  let availableState = 'available';
  const setAvailableState = () => {
    /**
     * - See variant-metafields.liquid for logic breakdown.
     * - needs to be in order of priority, highest priority at the end
     * - for example: hidden overwrites all the others and needs to be at the end
     */
    if (availability.showSoldOut === true || !available) {
      availableState = 'sold-out';
    }

    if (availability.relaunchTime > now && availability.showSoldOut === true) {
      availableState = 'back-soon';
    }

    if (availability.relaunchTime !== 0) {
      if (
        availability.relaunchTime < now &&
        availability.showSoldOut === true &&
        available
      ) {
        availableState = 'available';
      }
    }

    if (
      isOnCountryList(availability.marketsPublishVariant) &&
      availability.publishTime > now
    ) {
      availableState = 'hidden';
    }
  };
  setAvailableState();

  return availableState;
}
